/* -------------------------- Design imports start -------------------------- */
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import {
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";
import { useTranslation } from 'react-i18next'

import useMobileService from '../../hooks/useMobileService'
import Button from '../Button'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  title: string
  description?: string
  files?: string[]
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: () => void
}

/**
 * Modal that lists all selected files and asks if you are sure to delete them
 */
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function AccessFilesModal(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    title,
    description,
    files,
    onClose = () => null,
    onCancel = () => null,
    onConfirm = () => null,
  } = props
  const isMobile = useMobileService()
  let size
  isMobile ? (size = { height: '40%', width: '95%' }) : (size = { height: '50%', width: '40%' })
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const { t } = useTranslation()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleCancel = (): void => {
    setOpen(false)
    onCancel()
  }

  const handleConfirm = (): void => {
    setOpen(false)
    onConfirm()
  }

  const handleClose = (): void => {
    setOpen(false)
    onClose()
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: size.height,
          width: size.width,
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            width: '100%',
            paddingBottom: '16px',
          }}
        >
          <div>
            <Typography variant="h6" sx={{ marginBottom: '16px' }}>
              {title}
            </Typography>
            <div>
              <span>{description && description}</span>
            </div>
          </div>
          <div aria-label="accessFileModal-ListOfContent" style={{ overflow: 'auto' }}>
            <List>
              {files &&
                files.map((file, index) => {
                  if (file) {
                    return (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <InsertDriveFileOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={file} />
                      </ListItem>
                    )
                  } else {
                    return <div />
                  }
                })}
            </List>
          </div>
          <div
            aria-label="textfieldModal-actions"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Button id="cancelButton" color="inherit" variant="outlined" onClick={handleCancel}>
              {t('common:interaction.button.cancel')}
            </Button>
            <Button id="deleteButton" variant="contained" onClick={handleConfirm}>
              {t('common:interaction.button.delete')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Modal>
  )
}
